import { useEffect, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { formatDate } from 'shared_items/formatConventions';

/** Three pulsing dots while cover image loads (same keyframes as accounts principal). */
const ImageLoadingDots = () => (
  <div role="status" aria-label="Loading image" className="flex items-center justify-center gap-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-[gs-loading-dot-pulse_1.05s_ease-in-out_infinite]"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

const TripCover = ({ coverUrl }) => {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setBroken(false);
  }, [coverUrl]);

  const showImg = Boolean(coverUrl && !broken);

  return (
    <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden border flex flex-col items-center justify-center text-gray-400 relative">
      {showImg ? (
        <>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <ImageLoadingDots />
            </div>
          )}
          <img
            src={coverUrl}
            alt=""
            className={`w-full h-full object-cover transition-opacity duration-200 ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setLoaded(true)}
            onError={() => setBroken(true)}
          />
        </>
      ) : (
        <>
          <PhotoIcon className="h-6 w-6" />
          <span className="text-[10px] mt-1">No image</span>
        </>
      )}
    </div>
  );
};

const CardTrip = ({ trip, onClick }) => {
  const truncatedDescription = trip.description && trip.description.length > 60
    ? trip.description.slice(0, 60) + '...'
    : trip.description || 'No description';

  const isUpcoming = new Date(trip.start_date) > new Date();
  const coverUrl = trip.cover_url || null;

  return (
    <div
      className="bg-white p-4 rounded-xl shadow hover:bg-gray-50 cursor-pointer transition flex flex-row gap-4 relative"
      onClick={onClick}
    >
      {isUpcoming && (
        <div className="absolute top-1 right-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-sm border border-yellow-300">
          <span className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-yellow-700 rounded-full animate-pulse"></div>
            upcoming
          </span>
        </div>
      )}

      <TripCover coverUrl={coverUrl} />

      <div className="flex-1">
        <h3 className="text-md font-semibold mt-1">{trip.title || 'Untitled Trip'}</h3>
        <p className="text-sm text-gray-600">
          {formatDate(trip.start_date)} to {formatDate(trip.end_date)}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {truncatedDescription}
        </p>
      </div>
    </div>
  );
};

export default CardTrip;
